import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Textarea,
} from '@nextui-org/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const testCaseSchema = z.object({
  label: z.string().min(2, 'Label must be at least 2 characters'),
  chat_history: z.string(),
  target: z.string(),
});

type TestCaseFormData = z.infer<typeof testCaseSchema>;

interface TestCase {
  id: string;
  label: string;
  chat_history: string;
  target: string;
}

interface TestCaseSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  testCase?: TestCase;
  onSubmit: (data: TestCaseFormData) => void;
  isLoading?: boolean;
  isNewCase: boolean;
  handleDelete: (id: string) => void;
}

const TestCaseSidePanel = ({
  onClose,
  testCase,
  onSubmit,
  isLoading,
  isNewCase,
  handleDelete,
}: TestCaseSidePanelProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TestCaseFormData>({
    resolver: zodResolver(testCaseSchema),
    defaultValues: {
      label: '',
      chat_history: '',
      target: '',
    },
  });

  useEffect(() => {
    if (testCase) {
      reset({
        label: testCase.label,
        chat_history: testCase.chat_history,
        target: testCase.target,
      });
    }
  }, [testCase, reset]);

  return (
    <Card className="h-full min-h-0 rounded-none flex flex-col" shadow="none">
      <CardHeader className="flex justify-between items-center px-4 py-2 border-b shrink-0 p-0">
        <span className="text-large">
          {!isNewCase ? 'Edit Test Case' : 'Create Test Case'}
        </span>
        <div>
          {!isNewCase && testCase && (
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={() => handleDelete(testCase.id)}
            >
              <Icon icon="proicons:delete" width="20" />
            </Button>
          )}
          <Button isIconOnly variant="light" onPress={onClose}>
            <Icon icon="mdi:close" className="h-5 w-5" />
          </Button>
        </div>
      </CardHeader>
      <CardBody className="flex-grow overflow-y-auto p-0">
        <div className="space-y-4 py-4">
          <Controller
            name="label"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Label"
                isInvalid={!!errors.label}
                errorMessage={errors.label?.message}
              />
            )}
          />
          <Controller
            name="chat_history"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label="Chat History"
                isInvalid={!!errors.chat_history}
                errorMessage={errors.chat_history?.message}
              />
            )}
          />
          <Controller
            name="target"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label="Target"
                isInvalid={!!errors.target}
                errorMessage={errors.target?.message}
              />
            )}
          />
        </div>
      </CardBody>
      <div className="flex justify-between gap-2 p-0 text-right border-t mt-4 py-4">
        <Button variant="bordered" onPress={onClose} size="sm">
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isLoading}
          size="sm"
        >
          Save
        </Button>
      </div>
    </Card>
  );
};

export default TestCaseSidePanel;
